import { Box, Button, Container, Grid, Typography, Paper } from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import KitchenIcon from '@mui/icons-material/Kitchen';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    {
      icon: <RestaurantIcon sx={{ fontSize: 40, color: '#FE6B8B' }} />,
      title: t('home.features.menu.title'),
      description: t('home.features.menu.description'),
    },
    {
      icon: <KitchenIcon sx={{ fontSize: 40, color: '#FF8E53' }} />,
      title: t('home.features.kitchen.title'),
      description: t('home.features.kitchen.description'),
    },
    {
      icon: <PersonIcon sx={{ fontSize: 40, color: '#4CAF50' }} />,
      title: t('home.features.orders.title'),
      description: t('home.features.orders.description'),
    },
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: '#2196F3' }} />,
      title: t('home.features.support.title'),
      description: t('home.features.support.description'),
    },
  ];

  // Common Paper styles that can be reused for all grid items
  const commonPaperStyles = {
    p: 3,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    transition: '0.3s',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: 6,
    },
  };

  return (
    <Container maxWidth={false} sx={{ minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          background: 'white',
          borderRadius: 2,
          boxShadow: 1,
          my: 4,
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{ color: 'primary.main' }}
        >
          {t('home.welcome')}
        </Typography>
        <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary' }}>
          {t('home.subtitle')}
        </Typography>
      </Box>

      {/* Navigation Cards */}
      <Grid container spacing={4} sx={{ mb: 8 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={commonPaperStyles}>
            <RestaurantIcon sx={{ fontSize: 60, color: '#FE6B8B', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('home.cards.manager.title')}
            </Typography>
            <Typography sx={{ mb: 'auto', px: 1 }}>
              {t('home.cards.manager.description')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/manager/restaurants')}
              sx={{ mt: 2 }}
            >
              {t('home.cards.manager.button')}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={commonPaperStyles}>
            <KitchenIcon sx={{ fontSize: 60, color: '#FF8E53', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('home.cards.kitchen.title')}
            </Typography>
            <Typography sx={{ mb: 'auto', px: 1 }}>
              {t('home.cards.kitchen.description')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/kitchen-staff/restaurants')}
              sx={{
                mt: 2,
                bgcolor: '#FF8E53',
                '&:hover': { bgcolor: '#FF8E53' },
              }}
            >
              {t('home.cards.kitchen.button')}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={commonPaperStyles}>
            <RestaurantIcon sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('home.cards.waiter.title')}
            </Typography>
            <Typography sx={{ mb: 'auto', px: 1 }}>
              {t('home.cards.waiter.description')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/restaurant-waiter/restaurants')}
              sx={{
                mt: 2,
                bgcolor: '#4CAF50',
                '&:hover': { bgcolor: '#45a049' },
              }}
            >
              {t('home.cards.waiter.button')}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Paper elevation={3} sx={commonPaperStyles}>
            <PersonIcon sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {t('home.cards.customer.title')}
            </Typography>
            <Typography sx={{ mb: 'auto', px: 1 }}>
              {t('home.cards.customer.description')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/customer')}
              sx={{
                mt: 2,
                bgcolor: '#4CAF50',
                '&:hover': { bgcolor: '#4CAF50' },
              }}
            >
              {t('home.cards.customer.button')}
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Features Section */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h4" gutterBottom textAlign="center" sx={{ mb: 4 }}>
          {t('home.features.title')}
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                {feature.icon}
                <Typography variant="h6" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Contact Section */}
      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5', mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('home.contact.title')}
        </Typography>
        <Typography sx={{ mb: 2 }}>{t('home.contact.description')}</Typography>
        <Button
          variant="contained"
          startIcon={<EmailIcon />}
          href="mailto:contact@smartristo.ch"
          sx={{ bgcolor: '#2196F3', '&:hover': { bgcolor: '#1976D2' } }}
        >
          {t('home.contact.button')}
        </Button>
      </Paper>
    </Container>
  );
}
